import { useMemo, useState } from 'react';
import { useGmbPublishingStatus } from '../../hooks/useGmbGovernance';
import { resolveSectionId } from '../../utils/navigation';
import GMBEmptyState from './shared/GMBEmptyState';
import { GBMContentWrapper } from './shared/GMBLoadingState';

interface PublishingActivityMonitoringProps {
  onNavigate?: (section: string) => void;
}

const severityConfig: Record<string, { badgeBg: string; badgeText: string; border: string; accent: string; icon: string }> = {
  critical: {
    badgeBg: 'bg-red-100 dark:bg-red-900/30',
    badgeText: 'text-red-700 dark:text-red-300',
    border: 'border-red-200 dark:border-red-800',
    accent: 'text-red-600',
    icon: 'ri-error-warning-line',
  },
  warning: {
    badgeBg: 'bg-amber-100 dark:bg-amber-900/30',
    badgeText: 'text-amber-700 dark:text-amber-300',
    border: 'border-amber-200 dark:border-amber-800',
    accent: 'text-amber-600',
    icon: 'ri-alert-line',
  },
  informational: {
    badgeBg: 'bg-sky-100 dark:bg-sky-900/30',
    badgeText: 'text-sky-700 dark:text-sky-300',
    border: 'border-sky-200 dark:border-sky-800',
    accent: 'text-sky-600',
    icon: 'ri-information-line',
  },
};

function CadenceBar({ value, target }: { value: number; target: number }) {
  const pct = Math.min((value / target) * 100, 100);
  const color = pct >= 80 ? 'bg-teal-500' : pct >= 50 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="w-full h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
      <div className={`h-full ${color} rounded-full transition-all duration-500`} style={{ width: `${pct}%` }} />
    </div>
  );
}

export default function PublishingActivityMonitoring({ onNavigate }: PublishingActivityMonitoringProps) {
  const [severityFilter, setSeverityFilter] = useState('All');
  const [showCadenceDetail, setShowCadenceDetail] = useState(false);
  const { data: pubData, isLoading, error, refetch } = useGmbPublishingStatus();

  const data = pubData?.data || {};

  const cadenceCompliance = useMemo(() => {
    const cadence = data.weekly_cadence || [];
    const meeting = cadence.filter((w: any) => (w.posts_count || 0) >= (w.target || 0)).length;
    return Math.round((meeting / (cadence.length || 1)) * 100);
  }, [data]);

  const filteredRecommendations = useMemo(() => {
    const recs = data.publishing_recommendations || [];
    if (severityFilter === 'All') return recs;
    return recs.filter((r: any) => r.severity === severityFilter);
  }, [severityFilter, data]);

  const stats = [
    { label: 'Total Published', value: data.total_published ?? 0, icon: 'ri-send-plane-line', color: 'text-teal-600 bg-teal-50' },
    { label: 'This Month', value: data.this_month ?? 0, icon: 'ri-calendar-line', color: 'text-sky-600 bg-sky-50' },
    { label: 'Upcoming', value: data.upcoming_count ?? 0, icon: 'ri-calendar-schedule-line', color: 'text-violet-600 bg-violet-50' },
    { label: 'Expired', value: data.expired_count ?? 0, icon: 'ri-time-line', color: 'text-red-600 bg-red-50' },
  ];

  return (
    <GBMContentWrapper error={error} onRetry={() => refetch()} loading={isLoading}>
      <div className="space-y-5">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 shadow-sm flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative w-3 h-3">
              <div className="absolute inset-0 bg-amber-500 rounded-full animate-ping opacity-60" />
              <div className="relative w-3 h-3 bg-amber-500 rounded-full" />
            </div>
            <div>
              <div className="text-sm font-medium text-gray-800 dark:text-gray-200">Publishing Activity Monitoring Active</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Tracking content publishing consistency and generating recommendations for optimal GMB engagement
              </div>
            </div>
          </div>
          <div className="text-xs text-gray-400">Last updated: Just now</div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {stats.map((s) => (
            <div key={s.label} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-1.5">
                <div className={`w-7 h-7 ${s.color} rounded-md flex items-center justify-center`}>
                  <i className={`${s.icon} text-xs`} />
                </div>
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{s.label}</span>
              </div>
              <div className="text-xl font-bold text-gray-900 dark:text-white">{s.value}</div>
            </div>
          ))}
        </div>

        {data.is_inactive && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3">
            <div className="w-9 h-9 bg-red-100 dark:bg-red-800 flex items-center justify-center flex-shrink-0 rounded-md">
              <i className="ri-calendar-close-line text-red-500 text-sm" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-semibold text-red-700 dark:text-red-300">
                {data.inactivity_banner?.message || 'Your GMB profile has been inactive'}
              </div>
            </div>
            <button
              onClick={() => onNavigate?.(resolveSectionId(data.inactivity_banner?.cta_action) || 'post-scheduler')}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-xs font-medium rounded-md transition-colors cursor-pointer whitespace-nowrap"
            >
              {data.inactivity_banner?.cta_label || 'Schedule Now'}
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-teal-50 dark:bg-teal-900/20 rounded-md flex items-center justify-center text-teal-600">
                  <i className="ri-bar-chart-grouped-line text-sm" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Weekly Posting Cadence</h3>
                  <p className="text-[10px] text-gray-400">Per week</p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-teal-600">{cadenceCompliance}%</div>
                <div className="text-[10px] text-gray-400">on target</div>
              </div>
            </div>

            <div className="space-y-3">
              {(data.weekly_cadence || []).map((week: any) => (
                <div key={week.week_label} className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600 dark:text-gray-300 font-medium">{week.week_label}</span>
                    <span className="text-gray-500">{week.posts_count || 0} / {week.target || 0} posts</span>
                  </div>
                  <CadenceBar value={week.posts_count || 0} target={week.target || 0} />
                </div>
              ))}
            </div>

            <button
              onClick={() => setShowCadenceDetail(!showCadenceDetail)}
              className="text-xs text-teal-600 hover:text-teal-700 font-medium transition-colors cursor-pointer"
            >
              {showCadenceDetail ? 'Hide details' : 'View weekly breakdown'}
            </button>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-5 space-y-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-violet-50 dark:bg-violet-900/20 rounded-md flex items-center justify-center text-violet-600">
                <i className="ri-calendar-event-line text-sm" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Upcoming Scheduled Posts</h3>
                <p className="text-[10px] text-gray-400">{(data.upcoming_posts || []).length} posts in the queue</p>
              </div>
            </div>

            <div className="space-y-3">
              {(data.upcoming_posts || []).map((post: any, i: number) => (
                <div key={post.id || i} className="flex items-start gap-3 p-3 rounded-lg border border-gray-100 dark:border-gray-700 hover:border-gray-200 transition-colors">
                  <div className="w-10 h-10 bg-gray-50 dark:bg-gray-700 rounded-md flex flex-col items-center justify-center flex-shrink-0">
                    <i className="ri-calendar-line text-gray-400 text-xs" />
                    <span className="text-[9px] text-gray-500 dark:text-gray-400 font-medium leading-tight">{post.date || 'TBD'}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{post.title}</div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10px] text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">{post.google_post_type || post.type || 'Post'}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300">{post.status || 'Scheduled'}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => onNavigate?.('post-scheduler')}
                    className="text-xs text-teal-600 hover:text-teal-700 font-medium transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Edit
                  </button>
                </div>
              ))}
              {(data.upcoming_posts || []).length === 0 && (
                <GMBEmptyState title="No upcoming posts" message="Posts you schedule will appear here." icon="ri-calendar-event-line" />
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-5 space-y-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-red-50 dark:bg-red-900/20 rounded-md flex items-center justify-center text-red-600">
                <i className="ri-archive-line text-sm" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Expired Posts</h3>
                <p className="text-[10px] text-gray-400">{(data.expired_posts || []).length} posts past their effective date</p>
              </div>
            </div>
            <div className="space-y-2">
              {(data.expired_posts || []).map((post: any, i: number) => (
                <div key={post.id || i} className="flex items-center justify-between p-3 rounded-lg border border-red-100 dark:border-red-900/30 bg-red-50/30 dark:bg-red-900/10">
                  <div className="flex items-center gap-2">
                    <i className="ri-error-warning-line text-red-400 text-sm" />
                    <div>
                      <div className="text-sm text-gray-700 dark:text-gray-200">{post.title}</div>
                      <div className="text-[10px] text-gray-400">{post.post_type || 'Post'} · {post.expired_at_label}</div>
                    </div>
                  </div>
                  <span className="text-[10px] bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-2 py-0.5 rounded-full font-medium">Expired</span>
                </div>
              ))}
              {(data.expired_posts || []).length === 0 && (
                <p className="text-xs text-gray-500 dark:text-gray-400">No expired posts. Your calendar is up to date.</p>
              )}
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-5 space-y-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-amber-50 dark:bg-amber-900/20 rounded-md flex items-center justify-center text-amber-600">
                <i className="ri-pause-circle-line text-sm" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Inactive Periods</h3>
                <p className="text-[10px] text-gray-400">Gaps detected in posting activity</p>
              </div>
            </div>
            <div className="space-y-2">
              {(data.inactive_periods || []).map((period: any, i: number) => (
                <div key={i} className="flex items-start gap-3 p-3 rounded-lg border border-gray-100 dark:border-gray-700">
                  <div className="w-8 h-8 bg-gray-50 dark:bg-gray-700 rounded-md flex items-center justify-center flex-shrink-0">
                    <i className="ri-calendar-close-line text-gray-400 text-sm" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-200">{period.gap_label || 'Inactive gap'}</div>
                    <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">{period.start_date} — {period.end_date}</div>
                    <div className="text-[10px] text-amber-600 dark:text-amber-400 mt-0.5">
                      <i className="ri-time-line text-[10px] mr-1" />
                      {period.days} day{period.days > 1 ? 's' : ''} gap
                    </div>
                  </div>
                </div>
              ))}
              {(data.inactive_periods || []).length === 0 && (
                <p className="text-xs text-gray-500 dark:text-gray-400">No significant gaps. Posting is consistent.</p>
              )}
            </div>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Publishing Recommendations</h3>
            <select
              value={severityFilter}
              onChange={(e) => setSeverityFilter(e.target.value)}
              className="px-2.5 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-gray-800"
            >
              <option value="All">All Priorities</option>
              <option value="critical">Critical</option>
              <option value="warning">Warning</option>
              <option value="informational">Informational</option>
            </select>
          </div>

          <div className="space-y-3">
            {filteredRecommendations.map((rec: any, i: number) => {
              const c = severityConfig[rec.severity] || severityConfig.informational;
              return (
                <div key={i} className={`bg-white dark:bg-gray-800 rounded-lg border ${c.border} shadow-sm p-4 transition-all hover:shadow-md`}>
                  <div className="flex items-start gap-3">
                    <div className={`w-9 h-9 rounded-md ${c.badgeBg} flex items-center justify-center flex-shrink-0`}>
                      <i className={`${c.icon} text-sm`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${c.badgeBg} ${c.badgeText}`}>
                          {rec.severity}
                        </span>
                      </div>
                      <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200">{rec.title}</h4>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 leading-relaxed">{rec.description}</p>
                      {rec.cta_label && (
                        <div className="flex items-center gap-2 mt-3">
                          <button
                            onClick={() => onNavigate?.(resolveSectionId(rec.cta_action))}
                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-teal-600 hover:bg-teal-700 text-white rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap"
                          >
                            {rec.cta_label}
                            <i className="ri-arrow-right-line text-xs" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            {filteredRecommendations.length === 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-8 text-center shadow-sm">
                <div className="w-12 h-12 bg-green-50 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-check-double-line text-green-500 text-xl" />
                </div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-200">All recommendations addressed</p>
                <p className="text-xs text-gray-400 mt-1">Your publishing activity is on track.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </GBMContentWrapper>
  );
}